'use client'
// =============================================================================
// MemorySummary — widget MEM (right-panel). Source : GET /memory/dashboard
// (apps/api/src/routes/memory.routes.ts — déjà en prod pour /ai/memory,
// jamais dupliqué), mémoire 3 couches réelle (Episode/UserMemory Postgres —
// voir CLAUDE.md, Système de mémoire). Remplace l'ancien bloc MÉMOIRE
// (RightPanel.tsx) : "Demande: Hauteur zone R-1" figé, "42 documents" figé,
// et un arbre "Sainte-Adèle → Zone R-1 → Hauteur max 10m" 100% codé en dur —
// aucun n'avait de vraie donnée derrière. La section RELATIONS n'est PAS
// reconduite : aucune source de graphe réelle facilement branchable ici,
// jamais remplacée par une autre valeur inventée.
// =============================================================================
import { useEffect, useState } from 'react'
import { useSession } from 'next-auth/react'

interface LogEntry { id: string; agentSlug: string; action: string; createdAt: string }
interface FactEntry { id: string; key: string; value: string; updatedAt: string }
interface DashboardData {
  logs: LogEntry[]
  facts: FactEntry[]
  stats: { totalLogs: number; totalFacts: number; totalTokens: number }
}

function fmtAgo(iso?: string | null): string {
  if (!iso) return '—'
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000))
  if (s < 60) return `il y a ${s}s`
  const m = Math.round(s / 60)
  if (m < 60) return `il y a ${m} min`
  return `il y a ${Math.round(m / 60)} h`
}

export function MemorySummary() {
  const { data: session } = useSession()
  const [data, setData] = useState<DashboardData | null>(null)

  useEffect(() => {
    if (!session) return
    fetch('/api/memory/dashboard')
      .then(r => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => {})
  }, [session])

  if (!data) {
    return <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.15)', fontFamily: 'var(--font-mono)' }}>⟳ Chargement…</div>
  }

  const lastLog = data.logs[0]

  return (
    <>
      <div style={{
        padding: '10px 12px', borderRadius: 8,
        background: 'rgba(79,124,255,0.04)', border: '1px solid rgba(79,124,255,0.12)',
        marginBottom: 8, fontFamily: 'var(--font-mono)',
      }}>
        <div style={{ fontSize: 9, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.3)', marginBottom: 4 }}>ACTIVITÉ RÉCENTE</div>
        {lastLog ? (
          <>
            <div style={{ fontSize: 11, color: 'var(--text-primary)' }}>{lastLog.agentSlug} — {lastLog.action || '—'}</div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.2)', marginTop: 2 }}>{fmtAgo(lastLog.createdAt)}</div>
          </>
        ) : (
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>Aucun épisode enregistré</div>
        )}
      </div>

      <div style={{
        padding: '10px 12px', borderRadius: 8,
        background: 'rgba(52,211,153,0.04)', border: '1px solid rgba(52,211,153,0.12)',
        marginBottom: 8, fontFamily: 'var(--font-mono)',
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8,
      }}>
        <div>
          <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.2)' }}>Épisodes</div>
          <div style={{ fontSize: 14, color: 'var(--text-primary)', fontWeight: 600 }}>{data.stats.totalLogs}</div>
        </div>
        <div>
          <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.2)' }}>Faits retenus</div>
          <div style={{ fontSize: 14, color: 'var(--text-primary)', fontWeight: 600 }}>{data.stats.totalFacts}</div>
        </div>
      </div>

      <div style={{ fontSize: 9, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.2)', marginBottom: 6, fontFamily: 'var(--font-mono)' }}>
        FAITS RÉCENTS
      </div>
      {data.facts.length === 0 ? (
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.15)', fontFamily: 'var(--font-mono)' }}>Aucun fait mémorisé</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {data.facts.slice(0, 4).map(f => (
            <div key={f.id} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px',
              borderRadius: 6, background: 'rgba(255,255,255,0.02)', fontFamily: 'var(--font-mono)', fontSize: 10.5,
            }}>
              <span style={{ flex: 1, color: 'rgba(255,255,255,0.6)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {f.key} : {f.value}
              </span>
              <span style={{ fontSize: 8, color: 'rgba(255,255,255,0.15)', flexShrink: 0 }}>{fmtAgo(f.updatedAt)}</span>
            </div>
          ))}
        </div>
      )}
    </>
  )
}
